import {
  UtensilsCrossed, FileText, CheckCircle, Clock, Users 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal, FormField, FormInput, FormSelect, FormTextarea 
} from '@/components/ui/form-modal';
import {
  MenuCard, MenuDetailModal 
} from '@/features/menus';
import type {
  Menu, MenuStatus, MenuType 
} from '@/features/menus';
import type { Venue } from '@/features/settings';
import type { Recipe } from '@/features/recipes';
import {
  STATUS_TABS, MENU_TYPE_OPTIONS 
} from './constants';

interface MenuFormData {
  name: string;
  venueId: string;
  type: MenuType;
  description: string;
  effectiveFrom: string;
  effectiveTo: string;
  defaultPax: string;
}

interface OverviewViewProps {
  readonly menus: Menu[];
  readonly stats: {
    readonly total: number;
    readonly active: number;
    readonly draft: number;
    readonly totalItems: number;
    readonly totalPax: number;
  };
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  readonly selectedMenus: string[];
  readonly selectedStatus: MenuStatus | undefined;
  readonly setSelectedStatus: (status: MenuStatus | undefined) => void;
  readonly handleAddMenu: () => void;
  readonly handleEditMenu: () => void;
  readonly handleDeleteMenus: () => void;
  readonly handleBulkUpload: (file: File) => void;
  readonly exportData: Record<string, unknown>[];
  readonly showAddModal: boolean;
  readonly setShowAddModal: (show: boolean) => void;
  readonly editingMenu: string | null;
  readonly menuFormData: MenuFormData;
  readonly setMenuFormData: React.Dispatch<React.SetStateAction<MenuFormData>>;
  readonly handleSaveMenu: () => void;
  readonly venues: Venue[];
  readonly viewingMenu: Menu | null;
  readonly setViewingMenu: (menu: Menu | null) => void;
  readonly availableRecipes: Recipe[];
  readonly onSaveMenuItems: () => void;
  readonly onViewRecipe?: (recipe: Recipe) => void;
}

export function OverviewView({
  menus,
  stats,
  isLoading,
  error,
  refetch,
  selectedMenus,
  selectedStatus,
  setSelectedStatus,
  handleAddMenu,
  handleEditMenu,
  handleDeleteMenus,
  handleBulkUpload,
  exportData,
  showAddModal,
  setShowAddModal,
  editingMenu,
  menuFormData,
  setMenuFormData,
  handleSaveMenu,
  venues,
  viewingMenu,
  setViewingMenu,
  availableRecipes,
  onSaveMenuItems,
  onViewRecipe,
}: OverviewViewProps) {
  return (
    <>
      {/* Menu Detail Modal */}
      <MenuDetailModal
        menu={viewingMenu}
        isOpen={viewingMenu !== null}
        onClose={() => setViewingMenu(null)}
        onSave={() => onSaveMenuItems()}
        availableRecipes={availableRecipes}
        onViewRecipe={onViewRecipe}
      />

      {/* CRUD Toolbar */}
      <CrudToolbar
        entityName="Menu"
        entityNamePlural="Menus"
        selectedCount={selectedMenus.length}
        onAdd={handleAddMenu}
        onEdit={handleEditMenu}
        onDelete={handleDeleteMenus}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="menus.csv"
      />

      {/* Add/Edit Modal */}
      <FormModal
        title={editingMenu ? 'Edit Menu' : 'Add Menu'}
        description={editingMenu ? 'Update menu details' : 'Create a new menu'}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveMenu}
        saveLabel={editingMenu ? 'Update' : 'Create'}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Name" required>
            <FormInput
              value={menuFormData.name}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                name: e.target.value 
              })}
              placeholder="e.g., Main Dining Lunch"
            />
          </FormField>
          <FormField label="Venue" required>
            <FormSelect
              value={menuFormData.venueId}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                venueId: e.target.value 
              })}
              placeholder="Select venue..."
              options={venues.filter((v) => v.isActive).map((v) => ({
                value: v.venueId,
                label: v.name 
              }))}
            />
          </FormField>
          <FormField label="Type" required>
            <FormSelect
              value={menuFormData.type}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                type: e.target.value as MenuType 
              })}
              options={MENU_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label 
              }))}
            />
          </FormField>
          <FormField label="Default Pax">
            <FormInput
              type="number"
              min="0"
              value={menuFormData.defaultPax}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                defaultPax: e.target.value 
              })}
              placeholder="Expected guests"
            />
          </FormField>
          <FormField label="Effective From" required>
            <FormInput
              type="date"
              value={menuFormData.effectiveFrom}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                effectiveFrom: e.target.value 
              })}
            />
          </FormField>
          <FormField label="Effective To">
            <FormInput
              type="date"
              value={menuFormData.effectiveTo}
              onChange={(e) => setMenuFormData({
                ...menuFormData,
                effectiveTo: e.target.value 
              })}
            />
          </FormField>
        </div>
        <FormField label="Description">
          <FormTextarea
            value={menuFormData.description}
            onChange={(e) => setMenuFormData({
              ...menuFormData,
              description: e.target.value 
            })}
            placeholder="Menu description..."
            rows={2}
          />
        </FormField>
      </FormModal>

      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Menus</CardTitle>
            <FileText className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats.total}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-green-600">{stats.active}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Draft</CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-amber-600">{stats.draft}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Items</CardTitle>
            <UtensilsCrossed className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-blue-600">{stats.totalItems}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Daily Pax</CardTitle>
            <Users className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-purple-600">{stats.totalPax.toLocaleString()}</div></CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button key={tab.label} variant={selectedStatus === tab.value ? 'default' : 'outline'} size="sm" onClick={() => setSelectedStatus(tab.value)}>
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UtensilsCrossed className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load menus</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && menus.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UtensilsCrossed className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">No menus found</p>
            <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && menus.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {menus.map((menu) => (
            <div
              key={menu.menuId}
              className="relative rounded-lg transition-all cursor-pointer"
              onClick={() => setViewingMenu(menu)}
            >
              <MenuCard menu={menu} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
